import React from 'react'
import { useList } from '@refinedev/core'
import { DateField } from '@refinedev/antd'
import { Button, Skeleton, Space, Table, Typography } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, type Tone } from '../../theme/listkit'
import { kindSpec } from '../businesses/contentModeration'
import { useContentModeration } from '../businesses/useContentModeration'

/**
 * Replies-on-this-request table, rendered inside the Ask monitor's
 * `expandable.expandedRowRender` (Task P4) — lets an admin remove or
 * restore an owner reply or member recommendation without waiting for a
 * report first (see docs/JAGUAR-OWNED.md's Moderation section: replies are
 * public content on an Ask request, same ruling as a vouch on a listing).
 *
 * Reuses the P3 `useContentModeration('business_reply')` hook verbatim —
 * same confirm dialog, same messages, same double-submit guard — so an
 * admin write that actually changes a row lands in the admin audit log
 * exactly like one from `ReportedContent`, just without a report to stamp
 * (there isn't one).
 *
 * Refresh mechanism (fix round 2, item 1): this component makes NO refresh
 * call of its own after a remove/restore — no `refetch()`, no
 * `useInvalidate()`. `useContentModeration`'s own `useUpdate` (Refine core)
 * already invalidates `business_request_replies` list queries in its
 * internal `onSettled` — which fires for EVERY mutation outcome (success,
 * zero-row "success", or a thrown error) and runs BEFORE the per-call
 * `onSuccess`/`onError` that resolve `remove()`/`restore()`'s own promise —
 * using Refine's default `invalidates: ['list','many','detail']`. That
 * invalidation matches by resource name and query type, not by which
 * `useList` call asked for it, so it reaches BOTH this component's own list
 * AND the Ask monitor page's own reply-count query (`page.tsx`'s
 * `repliesQuery`, a separate `useList` on this same resource, filtered to
 * `request_id in (...)` + `status = 'visible'`) — no second, manual
 * invalidate call is needed to reach it, and adding one would only race the
 * automatic refetch the hook already started (`refetch()`'s default
 * `cancelRefetch: true` would cancel and restart it). A cancelled confirm
 * never calls `mutate()` at all, so nothing is invalidated and nothing
 * needed refreshing — the list was never stale. The one exception is the
 * error state's own "Try again" button below, a manual, user-initiated
 * `refetch()` with no automatic invalidation racing it.
 *
 * Acting lock (fix round 2, item 2): `actingId` is a COMPONENT-WIDE lock, not
 * a per-row one — it is set the instant ANY row's Remove/Restore is clicked
 * (before the confirm dialog even opens), and every row's button reads
 * `disabled={isPending || actingId !== null}`, so no other row can be
 * clicked while one is mid-flow. Only the acting row shows `loading`.
 * `actingId` clears only once the action has resolved (tracked by
 * `actionDone`) AND `query.isFetching` is false — an effect keyed on both:
 * a cancelled confirm never triggers a fetch, so the lock clears at once; a
 * real write's `useUpdate`-triggered refetch is already under way by the
 * time `remove()`/`restore()` resolves, so the effect waits for that fetch
 * (successful or not) to actually settle before releasing the lock.
 */

const KIND_LABEL: Record<string, string> = {
  owner_reply: 'Owner reply',
  member_recommendation: 'Recommendation',
}

interface ReplyAuthor {
  name: string | null
  class_year: string | null
}

interface ReplyBusiness {
  id: string
  name: string
}

interface ReplyRow {
  id: string
  request_id: string
  business_id: string
  kind: string
  body: string
  status: string
  created_at: string
  author?: ReplyAuthor | null
  business?: ReplyBusiness | null
}

function statusTone(status: string): Tone {
  return status === 'removed' ? 'red' : 'green'
}

function statusLabel(status: string): string {
  return status === 'removed' ? 'Removed' : 'Visible'
}

const mutedText: React.CSSProperties = { fontFamily: brand.fSans, fontSize: 13, color: brand.sub }

export function RequestReplies({ requestId }: { requestId: string }) {
  const spec = kindSpec('business_reply')!
  const { query, result } = useList<ReplyRow>({
    resource: 'business_request_replies',
    filters: [{ field: 'request_id', operator: 'eq', value: requestId }],
    sorters: [{ field: 'created_at', order: 'asc' }],
    pagination: { mode: 'off' },
    meta: { select: spec.select },
    // No automatic retry: this expanded-row table has its own manual "Try
    // again" button and should surface a failure right away, not spend
    // several seconds silently retrying behind the loading skeleton.
    queryOptions: { retry: false },
  })
  const { remove, restore, isPending } = useContentModeration('business_reply')
  // Component-wide acting lock (fix round 2, item 2) — see the module doc
  // comment above for the full clear-timing rationale.
  const [actingId, setActingId] = React.useState<string | null>(null)
  const [actionDone, setActionDone] = React.useState(false)

  React.useEffect(() => {
    if (actionDone && !query.isFetching) {
      setActingId(null)
      setActionDone(false)
    }
  }, [actionDone, query.isFetching])

  const rows = result?.data ?? []
  const locked = isPending || actingId !== null

  async function handleRemove(id: string) {
    setActingId(id)
    await remove(id)
    setActionDone(true)
  }

  async function handleRestore(id: string) {
    setActingId(id)
    await restore(id)
    setActionDone(true)
  }

  if (query.isLoading) return <Skeleton active />

  if (query.isError) {
    return (
      <Space direction="vertical" size={8}>
        <Typography.Text style={mutedText}>Couldn&apos;t load replies.</Typography.Text>
        <Button size="small" onClick={() => query.refetch()}>
          Try again
        </Button>
      </Space>
    )
  }

  if (rows.length === 0) {
    return <Typography.Text style={{ ...mutedText, fontStyle: 'italic' }}>No replies.</Typography.Text>
  }

  return (
    <Table<ReplyRow> dataSource={rows} rowKey="id" pagination={false} scroll={{ x: true }}>
      <Table.Column<ReplyRow>
        title="Author"
        render={(_: unknown, row) => (
          <span style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.ink }}>
            {row.author?.name ?? 'Unknown member'}
            {row.author?.class_year ? ` · Class of ${row.author.class_year}` : ''}
          </span>
        )}
      />
      <Table.Column<ReplyRow> title="Kind" dataIndex="kind" render={(v: string) => KIND_LABEL[v] ?? v} />
      <Table.Column<ReplyRow> title="Business" render={(_: unknown, row) => row.business?.name ?? '—'} />
      <Table.Column<ReplyRow> title="Reply" dataIndex="body" />
      <Table.Column<ReplyRow>
        title="Status"
        render={(_: unknown, row) => <Pill tone={statusTone(row.status)}>{statusLabel(row.status)}</Pill>}
      />
      <Table.Column<ReplyRow>
        title="Date"
        dataIndex="created_at"
        render={(v: string) => <DateField value={v} format="MMM D, YYYY" />}
      />
      <Table.Column<ReplyRow>
        title="Actions"
        render={(_: unknown, row) =>
          row.status === 'removed' ? (
            <Button
              size="small"
              loading={actingId === row.id}
              disabled={locked}
              data-testid={`reply-restore-${row.id}`}
              onClick={() => void handleRestore(row.id)}
            >
              Restore
            </Button>
          ) : (
            <Button
              size="small"
              danger
              loading={actingId === row.id}
              disabled={locked}
              data-testid={`reply-remove-${row.id}`}
              onClick={() => void handleRemove(row.id)}
            >
              Remove
            </Button>
          )
        }
      />
    </Table>
  )
}
